/**
 * UX_LEXICON - User Platform Lexicon (SSOT)
 * 
 * Single Source of Truth для всех user-facing текстов в empty/error states.
 * Используется в EmptyState, ErrorState и других компонентах.
 * 
 * Canon: CANON.md v1.3.0, PRD-USER-001
 * 
 * Правила:
 * - Все тексты человеко-понятные (no technical jargon)
 * - Всегда есть title + description
 * - CTA labels четкие и actionable
 * - Поддержка i18n (en/ru)
 */

export const UX_LEXICON = {
  dashboard: {
    empty: {
      title: 'Welcome to Supabank',
      description: 'Get started by setting up your accounts. Create a crypto wallet to receive payments, or connect a bank account for payouts.',
      action: {
        createWallet: 'Create crypto wallet',
        addBank: 'Add bank account',
        viewAddresses: 'View addresses',
      },
    },
    error: {
      title: 'Failed to load dashboard',
      message: 'We couldn\'t load your dashboard information. This might be a temporary network issue.',
      retry: 'Retry',
      contactSupport: 'Contact support',
    },
  },

  wallet: {
    balances: {
      empty: {
        title: 'No balances yet',
        description: 'As soon as funds arrive, you\'ll see them here. Create a crypto wallet to start receiving payments.',
        action: {
          receive: 'Receive crypto',
          addFunds: 'Add funds',
          createWallet: 'Create wallet',
        },
      },
      error: {
        title: 'Failed to load wallet balances',
        message: 'We couldn\'t load your wallet balances. Please check your connection and try again.',
        retry: 'Retry',
      },
    },
    addresses: {
      empty: {
        title: 'No crypto wallets yet',
        description: 'Your crypto wallets will appear here once they are created. Create your first wallet to start receiving cryptocurrency payments.',
        action: {
          create: 'Create wallet',
          createAddress: 'Create address',
        },
      },
      error: {
        title: 'Failed to load crypto wallets',
        message: 'We couldn\'t load your crypto wallets. Please try again.',
        retry: 'Retry',
      },
    },
  },

  transactions: {
    empty: {
      noFilters: {
        title: 'No transactions yet',
        description: 'You haven\'t made any transactions yet. Your transaction history will appear here once you start using the platform.',
      },
      withFilters: {
        title: 'No transactions found',
        description: 'No transactions match your filters. Try adjusting your search criteria.',
        action: {
          clearFilters: 'Clear filters',
        },
      },
    },
    error: {
      title: 'Transactions temporarily unavailable',
      message: 'An error occurred while loading transactions. Please try again.',
      retry: 'Retry',
    },
  },

  address: {
    empty: {
      title: 'No crypto wallet yet',
      description: 'Create a crypto wallet to start receiving payments that will be automatically converted to cash.',
      action: {
        create: 'Create wallet',
        generate: 'Generate address',
      },
    },
    error: {
      title: 'Failed to load addresses',
      message: 'We couldn\'t load your crypto addresses. Please try again.',
      retry: 'Retry',
    },
  },

  kyc: {
    approved: {
      title: 'Verification complete',
      description: 'You\'re verified! Continue to choose how you\'d like to start using Supabank.',
      action: {
        continue: 'Continue to next step',
      },
    },
    rejected: {
      title: 'Verification needs attention',
      message: 'We couldn\'t verify your identity. This usually means we need clearer photos or additional information.',
      actions: {
        retry: 'Upload new documents',
        contactSupport: 'Contact support',
      },
    },
    pending: {
      title: 'Documents under review',
      description: 'Your verification documents are being reviewed. This usually takes a few minutes.',
      action: {
        refresh: 'Refresh status',
      },
    },
    processing: {
      title: 'Verification in progress',
      description: 'Your documents have been submitted and are being reviewed. This usually takes 1-2 business days.',
      action: {
        refresh: 'Refresh status',
      },
    },
    // NEW: incomplete — пользователь начал, но не закончил
    incomplete: {
      title: 'Finish your verification',
      description: 'You\'ve started the verification process but haven\'t completed all steps. Continue where you left off.',
      action: {
        continue: 'Continue verification',
        restart: 'Start over',
      },
    },
    // NEW: postSubmission — документы поданы, ждём проверки (нет активной ссылки)
    postSubmission: {
      title: 'We\'re reviewing your documents',
      description: 'Your documents have been submitted successfully. We\'ll notify you once the review is complete, usually within 1-2 business days.',
      action: {
        refresh: 'Check status',
      },
    },
    // NEW: expired — ссылка верификации истекла
    expired: {
      title: 'Verification link expired',
      description: 'Your verification session has expired. Please start a new verification to continue.',
      action: {
        restart: 'Start new verification',
      },
    },
    resume: {
      title: 'Continue your verification',
      description: 'It looks like you have an in-progress session. Open the verification link to finish.',
      actions: {
        openLink: 'Open verification link',
        refresh: 'I\'m done',
        checkStatus: 'Check verification status',
        reopenForm: 'Re-open verification form',
      },
      // NEW: In-progress state after user opens link
      inProgress: {
        title: 'Verification in progress',
        description: 'Complete the form in the other tab. This page updates automatically.',
        waiting: 'Waiting for verification results...',
        automatedChecks: 'Automated checks: 1-5 minutes',
        manualReview: 'Manual review (if needed): up to 24 hours',
        checkPrompt: 'Already completed the form? Check your status now.',
      },
    },
    notStarted: {
      title: 'Start verification',
      description: 'Launch our secure form to verify your identity. This only takes a few minutes.',
      action: {
        launch: 'Launch verification',
      },
      hint: 'Accept the Terms of Service above before launching verification.',
    },
    tos: {
      mustReview: 'Please open the Terms of Service before accepting.',
      errorFetching: 'We couldn\'t load the Terms of Service. Please try again.',
    },
    error: {
      network: {
        title: 'Verification temporarily unavailable',
        message: 'We couldn\'t connect to the verification service. Please check your connection and try again.',
        retry: 'Retry',
      },
      launch: {
        title: 'Failed to start verification',
        message: 'We couldn\'t start the verification process. Please try again or contact support if the issue persists.',
        retry: 'Try again',
        contactSupport: 'Contact support',
      },
      tosFetch: {
        title: 'Terms of Service unavailable',
        message: 'We couldn\'t load the Terms of Service. Please try again.',
        retry: 'Try again',
      },
    },
  },

  // Generic error messages (fallback)
  errors: {
    network: {
      title: 'Connection issue',
      message: 'We can\'t load the data right now. Check your connection and try again.',
      retry: 'Retry',
    },
    provider: {
      title: 'Service temporarily unavailable',
      message: 'The service is temporarily unavailable. Please try again in a moment.',
      retry: 'Retry',
    },
    unknown: {
      title: 'Something went wrong',
      message: 'An unexpected error occurred. Please try again or contact support if the issue persists.',
      retry: 'Try again',
      contactSupport: 'Contact support',
    },
  },

  // Beta & Compliance Banner
  betaCompliance: {
    banner: {
      title: 'Beta Launch: We\'re Just Getting Started',
      description: 'You\'re using Supabank during our beta launch. We recommend starting with smaller amounts as we continue to refine the platform. Your funds are secure and held in custodial ledgers with full transparency. We\'re building this right, and your feedback helps us improve.',
      cookies: {
        title: 'Cookies and Data',
        description: 'We use essential cookies for authentication and session management. We collect minimal data necessary for service operation and comply with GDPR requirements. Your data is processed transparently and securely.',
      },
      gdpr: {
        title: 'GDPR Compliance',
        description: 'We are committed to GDPR compliance. You have the right to access, rectify, erase, and object to processing of your personal data. All data processing is transparent and lawful.',
      },
      action: {
        learnMore: 'Learn more',
        privacyPolicy: 'Privacy Policy',
        dismiss: 'Got it',
      },
    },
  },
} as const;

/**
 * Helper function to get human-readable error message from error object
 */
export function humanizeError(error: unknown): { title: string; message: string } {
  if (error instanceof Error) {
    const errorMessage = error.message.toLowerCase();
    
    // Network errors
    if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('connection')) {
      return {
        title: UX_LEXICON.errors.network.title,
        message: UX_LEXICON.errors.network.message,
      };
    }
    
    // Provider/service errors
    if (errorMessage.includes('provider') || errorMessage.includes('service') || errorMessage.includes('unavailable')) {
      return {
        title: UX_LEXICON.errors.provider.title,
        message: UX_LEXICON.errors.provider.message,
      };
    }
    
    // KYC-specific errors
    if (errorMessage.includes('kyc') || errorMessage.includes('verification')) {
      if (errorMessage.includes('rejected')) {
        return {
          title: UX_LEXICON.kyc.rejected.title,
          message: UX_LEXICON.kyc.rejected.message,
        };
      }
      return {
        title: UX_LEXICON.kyc.error.network.title,
        message: UX_LEXICON.kyc.error.network.message,
      };
    }
  }
  
  // Fallback to unknown error
  return {
    title: UX_LEXICON.errors.unknown.title,
    message: UX_LEXICON.errors.unknown.message,
  };
}

